import styles from './index.module.css'

import { LuPlus } from '@react-icons/all-files/lu/LuPlus'
import { LuSearch } from '@react-icons/all-files/lu/LuSearch'
import { LuX } from '@react-icons/all-files/lu/LuX'
import cn from 'clsx'
import React, { useEffect, useMemo, useState } from 'react'

import mixpanel from '@disco/api/mixpanel'
import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'
import { createCustomNativeTag } from '@disco/state-management/actions/CustomNativeTagActions'
import { applyNativeTagsToTracks } from '@disco/state-management/actions/PlaylistTrackActions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getNativeCategories,
  getNativeTags,
} from '@disco/state-management/selectors/tags'
import { matchTag } from '@disco/utils/tagSearch'

import type { State } from 'types/redux'
import type { NativeCategory, NativeTag } from 'types/track'

import {
  Button,
  Dialog,
  Flex,
  IconButton,
  Link,
  Text,
  TextField,
  Theme,
} from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { isAiTagPlaceholder } from './ai-tag-placeholder'
import TagChip from './tag-chip'

/** Rail scope for browsing/searching every category at once. */
const ALL_TAGS = 'all'

type Scope = number | typeof ALL_TAGS

type Props = {
  trackIds: number[]
  assignedTags: NativeTag[]
  onClose: () => void
}

const byName = (a: NativeTag, b: NativeTag) => a.name.localeCompare(b.name)

/**
 * "Add track tags" modal: a category rail on the left (with a pinned
 * "All tags" scope) and a chip grid on the right. One search input follows
 * the rail scope — in "All tags" it searches everything grouped by category,
 * inside a category it filters that category and surfaces matches from other
 * categories as a quiet spillover group. Selection is staged locally and
 * applied to the tracks when the user hits Done.
 * @param trackIds - Tracks the tag changes are applied to
 * @param assignedTags - Tags currently on the tracks (initial selection)
 * @param onClose - Called when the modal closes, saved or not
 */
const AddTrackTagsModal = ({ trackIds, assignedTags, onClose }: Props) => {
  const dispatch = useDispatch()
  const categories: NativeCategory[] = useSelector(getNativeCategories)
  const allTags = useSelector(getNativeTags)
  const colorIndicatorsEnabled = useFeatureEnabled(COLOR_INDICATORS)
  const isNativeTagsReadonly: boolean = Boolean(
    useSelector(
      (state: State) =>
        state.user.business?.private_metadata.is_native_tags_readonly,
    ),
  )

  const initialIds = useMemo(
    () => assignedTags.map(tag => tag.id),
    [assignedTags],
  )
  const [selectedIds, setSelectedIds] = useState<number[]>(initialIds)
  // Snapshot of the selection used only for ORDERING the grid. It refreshes
  // when the user changes scope (not on every toggle), so chips don't jump
  // to the top mid-selection — they surface on the next visit.
  const [orderIds, setOrderIds] = useState<number[]>(initialIds)
  const [scope, setScope] = useState<Scope>(ALL_TAGS)
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createSaving, setCreateSaving] = useState(false)
  const [saving, setSaving] = useState(false)
  const [freshTagId, setFreshTagId] = useState<number | null>(null)

  useEffect(() => {
    if (freshTagId === null) return
    const timer = setTimeout(() => setFreshTagId(null), 400)
    return () => clearTimeout(timer)
  }, [freshTagId])

  const activeCategory =
    scope === ALL_TAGS ? undefined : categories.find(cat => cat.id === scope)

  const toggleTag = (tag: NativeTag) => {
    setSelectedIds(ids =>
      ids.includes(tag.id) ? ids.filter(id => id !== tag.id) : [...ids, tag.id],
    )
  }

  const cancelCreate = () => {
    setCreating(false)
    setCreateName('')
  }

  const startCreate = (prefill: string) => {
    setCreating(true)
    setCreateName(prefill)
  }

  const switchScope = (next: Scope) => {
    setScope(next)
    setOrderIds(selectedIds)
    cancelCreate()
    // Keep the query when hopping between categories (handy with spillover
    // results); reset it when returning to the full overview.
    if (next === ALL_TAGS) setQuery('')
  }

  const commitCreate = async () => {
    const name = createName.trim()
    if (!name || !activeCategory) return
    const existing = activeCategory.native_tags.find(
      tag => tag.name.toLowerCase() === name.toLowerCase(),
    )

    if (existing) {
      setSelectedIds(ids =>
        ids.includes(existing.id) ? ids : [...ids, existing.id],
      )
      // Surface the tag at the top right away — creating is an explicit
      // action, unlike toggling, so the jump is expected.
      setOrderIds(ids =>
        ids.includes(existing.id) ? ids : [...ids, existing.id],
      )
      setFreshTagId(existing.id)
      cancelCreate()
      setQuery('')
      return
    }

    setCreateSaving(true)
    try {
      const tag = await dispatch(createCustomNativeTag(name, activeCategory.id))
      setSelectedIds(ids => [...ids, tag.id])
      setOrderIds(ids => [...ids, tag.id])
      setFreshTagId(tag.id)
      cancelCreate()
      setQuery('')
    } catch (_error) {
      // Creation failed — leave the form open so the user can retry.
    } finally {
      setCreateSaving(false)
    }
  }

  const handleDone = async () => {
    const addedIds = selectedIds.filter(id => !initialIds.includes(id))
    const removedIds = initialIds.filter(id => !selectedIds.includes(id))

    if (!addedIds.length && !removedIds.length) {
      onClose()
      return
    }

    if (addedIds.length) {
      const tagNames = allTags
        .filter(tag => addedIds.includes(tag.id))
        .map(tag => tag.name)
      mixpanel.sendPureEvent(
        trackIds.length > 1 ? 'Add multiple native tags' : 'Add native tag',
        { Tags: tagNames },
      )
    }

    setSaving(true)
    try {
      await dispatch(applyNativeTagsToTracks(trackIds, addedIds, removedIds))
    } catch (_) {
      setSaving(false)
      return
    }
    setSaving(false)
    onClose()
  }

  const trimmedQuery = query.trim()

  /** Pinned-first ordering inside one category, driven by the snapshot. */
  const orderCategoryTags = (tags: NativeTag[]) => {
    const pinned = tags.filter(tag => orderIds.includes(tag.id))
    const rest = tags.filter(tag => !orderIds.includes(tag.id))
    return [...pinned.sort(byName), ...rest.sort(byName)]
  }

  /* "All tags" view: every category as a group, filtered by the query. */
  const allGroups = useMemo(() => {
    if (scope !== ALL_TAGS) return []
    return categories
      .map(category => ({
        category,
        tags: orderCategoryTags(
          trimmedQuery
            ? category.native_tags.filter(tag =>
                matchTag(tag.name, trimmedQuery),
              )
            : category.native_tags,
        ),
      }))
      .filter(group => group.tags.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, categories, trimmedQuery, orderIds])

  /* Category view: the active category's tags, filtered by the query. */
  const categoryTags = useMemo(() => {
    if (!activeCategory) return []
    return orderCategoryTags(
      trimmedQuery
        ? activeCategory.native_tags.filter(tag =>
            matchTag(tag.name, trimmedQuery),
          )
        : activeCategory.native_tags,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, trimmedQuery, orderIds])

  const selectedCountFor = (category: NativeCategory) =>
    category.native_tags.filter(tag => selectedIds.includes(tag.id)).length

  const renderChip = (tag: NativeTag) => (
    <TagChip
      key={tag.id}
      name={tag.name}
      color={colorIndicatorsEnabled ? tag.color : undefined}
      ai={isAiTagPlaceholder(tag)}
      selected={selectedIds.includes(tag.id)}
      fresh={tag.id === freshTagId}
      onToggle={() => toggleTag(tag)}
    />
  )

  const renderGroupHead = (label: string) => (
    <div className={styles.groupHead}>
      <span>{label}</span>
      <span className={styles.groupLine} />
    </div>
  )

  const summary = `${selectedIds.length} ${
    selectedIds.length === 1 ? 'tag' : 'tags'
  } ${trackIds.length > 1 ? 'selected' : 'on this track'}`

  const searchPlaceholder = activeCategory
    ? `Search ${activeCategory.name.toLowerCase()}…`
    : 'Search all tags…'

  return (
    <Dialog.Root open onOpenChange={open => !open && onClose()}>
      <Dialog.Content
        maxWidth="720px"
        width="720px"
        className={styles.content}
        aria-describedby={undefined}
        onEscapeKeyDown={event => {
          if (creating) {
            event.preventDefault()
            cancelCreate()
          } else if (query) {
            event.preventDefault()
            setQuery('')
          }
        }}
      >
        <Theme className={styles.shell}>
          <Flex align="center" justify="between" className={styles.header}>
            <Dialog.Title className={styles.title}>Add track tags</Dialog.Title>
            <Dialog.Close>
              <IconButton
                type="button"
                variant="ghost"
                color="gray"
                aria-label="Close"
              >
                <LuX size={16} />
              </IconButton>
            </Dialog.Close>
          </Flex>

          <div className={styles.cols}>
            <div className={styles.rail}>
              <button
                type="button"
                className={cn(styles.railItem, {
                  [styles.railItemOn]: scope === ALL_TAGS,
                })}
                onClick={() => switchScope(ALL_TAGS)}
              >
                <span className={styles.railLabel}>All tags</span>
                {selectedIds.length > 0 && (
                  <span className={styles.railCount}>{selectedIds.length}</span>
                )}
              </button>
              <div className={styles.railSep} />
              {categories.map(category => {
                const count = selectedCountFor(category)
                return (
                  <button
                    type="button"
                    key={category.id}
                    className={cn(styles.railItem, {
                      [styles.railItemOn]: category.id === scope,
                    })}
                    onClick={() => switchScope(category.id)}
                  >
                    <span className={styles.railLabel}>{category.name}</span>
                    {count > 0 && (
                      <span className={styles.railCount}>{count}</span>
                    )}
                  </button>
                )
              })}
            </div>

            <div className={styles.main}>
              <div className={styles.searchRow}>
                <TextField.Root
                  size="2"
                  className={styles.search}
                  placeholder={searchPlaceholder}
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                >
                  <TextField.Slot>
                    <LuSearch size={14} />
                  </TextField.Slot>
                  {query && (
                    <TextField.Slot side="right">
                      <IconButton
                        type="button"
                        size="1"
                        variant="ghost"
                        color="gray"
                        aria-label="Clear search"
                        onClick={() => setQuery('')}
                      >
                        <LuX size={13} />
                      </IconButton>
                    </TextField.Slot>
                  )}
                </TextField.Root>
                {activeCategory &&
                  !isNativeTagsReadonly &&
                  (creating ? (
                    <form
                      className={styles.createForm}
                      onSubmit={event => {
                        event.preventDefault()
                        commitCreate()
                      }}
                    >
                      <TextField.Root
                        size="2"
                        autoFocus
                        placeholder={`New ${activeCategory.name.toLowerCase()} tag…`}
                        value={createName}
                        onChange={event => setCreateName(event.target.value)}
                      />
                      <Button
                        type="submit"
                        size="2"
                        loading={createSaving}
                        disabled={!createName.trim()}
                      >
                        Create
                      </Button>
                      <Button
                        type="button"
                        size="2"
                        variant="ghost"
                        color="gray"
                        onClick={cancelCreate}
                      >
                        Cancel
                      </Button>
                    </form>
                  ) : (
                    <Button
                      type="button"
                      size="2"
                      variant="ghost"
                      onClick={() => startCreate('')}
                    >
                      <LuPlus size={14} />
                      New tag
                    </Button>
                  ))}
              </div>

              <div className={styles.tagGrid}>
                {scope === ALL_TAGS ? (
                  allGroups.length === 0 ? (
                    <div className={styles.gridEmpty}>
                      <span>No matching tags</span>
                    </div>
                  ) : (
                    allGroups.map(({ category, tags }) => (
                      <div key={category.id} className={styles.group}>
                        {renderGroupHead(category.name)}
                        <div className={styles.groupFlow}>
                          {tags.map(tag => renderChip(tag))}
                        </div>
                      </div>
                    ))
                  )
                ) : categoryTags.length === 0 ? (
                  <div className={styles.gridEmpty}>
                    <span>
                      {`No ${activeCategory?.name.toLowerCase() ?? ''} match`}
                    </span>
                    {!isNativeTagsReadonly && trimmedQuery && (
                      <>
                        <span> — </span>
                        <button
                          type="button"
                          className={styles.createLink}
                          onClick={() => startCreate(trimmedQuery)}
                        >
                          create “{trimmedQuery}”
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className={styles.groupFlow}>
                    {categoryTags.map(tag => renderChip(tag))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <Flex align="center" justify="between" className={styles.footer}>
            <Flex align="center" gap="4">
              <Text size="1" color="gray">
                {summary}
              </Text>
              <Link size="1" href="/settings/track-tags">
                Manage tags
              </Link>
            </Flex>
            <Flex align="center" gap="3">
              <Button
                type="button"
                size="2"
                variant="soft"
                color="gray"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="2"
                loading={saving}
                onClick={handleDone}
              >
                Done
              </Button>
            </Flex>
          </Flex>
        </Theme>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default AddTrackTagsModal
